// stream_token.ts — M2.1 Wave 7 ARC-9 client.
//
// POST /api/v1/sessions/{id}/stream-token/refresh
//
// Why: SSE stream tokens carry a 5-min TTL (security cap on the query-string
// credential). Long sessions under gating_mode=user_advance can run for up to
// 24 h; the FE EventSource reconnect would otherwise need to re-POST /sessions
// (and re-spawn the council) to mint a fresh token. This endpoint trades the
// still-held (likely expired) stream token + the user's Clerk JWT for a fresh
// 5-min token with no council impact.
//
// BE wire (internal/api/stream_token_refresh.go):
//   POST /api/v1/sessions/{id}/stream-token/refresh
//   body : { "old_token": "<32-hex>" }
//   200  : { "stream_token": "<32-hex>", "sse_url": "/api/v1/sessions/{id}/sse?token=…" }
//   400  : stream_token_invalid             — body parse / empty old_token
//   401  : clerk_jwt_invalid                — Clerk auth failed
//   403  : session_unauthorized             — token belongs to a different user/session
//   404  : session_not_found                — old token never minted by this process
//
// Errors are surfaced as ApiError(status, errorCode) per FIX-C2 flat envelope.

import { ApiError } from './client';
import type { ApiError as ApiErrorBody } from '@/types/api';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) || '/api/v1';

/**
 * StreamTokenRefreshResponse — BE 200 body shape.
 *
 * `stream_token` is the new 32-hex token (re-attach as `?token=` query
 * param on a new EventSource). `sse_url` is a convenience pre-built URL
 * already carrying the new token; the SSE consumer SHOULD prefer it over
 * re-stitching to avoid drift with BE path conventions.
 */
export interface StreamTokenRefreshResponse {
  stream_token: string;
  sse_url: string;
}

/**
 * refreshStreamToken — POST /sessions/{id}/stream-token/refresh.
 *
 * Reads VITE_API_BASE the same way client.ts does. Uses the caller's Clerk
 * JWT (already-fetched) directly rather than the global token getter so the
 * SSE hook can wire refresh without coupling to the React auth bootstrap.
 *
 * Throws ApiError on non-2xx with `code` populated from FLAT envelope
 * `body.error_code` (canonical M2 shape per BE_SPEC §8.3).
 */
export async function refreshStreamToken(
  sessionId: string,
  oldToken: string,
  clerkToken: string,
): Promise<StreamTokenRefreshResponse> {
  const res = await fetch(
    `${API_BASE}/sessions/${sessionId}/stream-token/refresh`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${clerkToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ old_token: oldToken }),
    },
  );

  if (!res.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = { error: 'unknown', message: res.statusText };
    }
    const rec = body as Record<string, unknown>;
    const code = typeof rec.error_code === 'string'
      ? rec.error_code
      : typeof rec.code === 'string'
        ? rec.code
        : 'unknown';
    const message = typeof rec.message === 'string' ? rec.message : res.statusText;
    const err = new ApiError(res.status, code, message, body);
    err.traceId = res.headers.get('X-Trace-ID') ||
      (typeof rec.trace_id === 'string' ? rec.trace_id : undefined);
    throw err;
  }

  return (await res.json()) as StreamTokenRefreshResponse;
}
